import { useMemo } from 'react';
import type { AreaScope } from '@releve/shared';
import { useIsSuperAdmin } from '@/lib/permissions';
import { useCurrentUserOrgRole } from '@/hooks/useCurrentUserOrgRole';
import { useEnabledFeatures } from '@/hooks/useOrganizationFeatures';

/**
 * The gates a nav entry can carry — primary + tools items
 * (`navigationItems.ts`), secondary + project sidebar items (`navigation.ts`),
 * quick-add items (`quickAddItems.ts`) and entity detail tabs
 * (`entityDetailTabs.ts`). ONE filter ({@link isNavItemVisible}) evaluates all
 * of them, so a gate means the same thing on every surface
 * (docs/NAVIGATION.md "Visibility gates").
 */
export interface NavGates {
  /** Platform super admins only. */
  requiresSuperAdmin?: boolean;
  /** Org admins (and super admins, who have no membership row). */
  requiresOrgAdmin?: boolean;
  /**
   * The org-role scope the destination needs (`@releve/shared` `orgRoleHasScope`):
   * `accounting` hides Accounting, Ré and financial surfaces from a
   * project manager; `accounting_admin` keeps the Audit Log to admins and
   * accountants. A super admin is judged like everyone else (a non-member
   * super admin counts as `member`) unless `superAdminBypass` is set — the
   * same rule as the item's `RequireOrgScope` route guard.
   */
  requiresScope?: AreaScope;
  /**
   * With `requiresScope`: a platform super admin sees the entry whatever
   * their role. Set it exactly where the route guard has `superAdminBypass`
   * (today only the Audit Log).
   */
  superAdminBypass?: boolean;
  /** Org feature flag that must be enabled. */
  featureFlag?: string;
}

export interface NavVisibilityContext {
  isSuperAdmin: boolean;
  isAdmin: boolean;
  can: (scope: AreaScope) => boolean;
  /**
   * The org role is still loading. A `requiresScope` entry is then treated as
   * VISIBLE: three of the four roles carry `accounting`, so showing it while
   * unknown keeps the common case still (no hide-then-show of Accounting); a
   * project manager may see it for that first moment, and the route guard
   * still redirects. Optional — absent means loaded.
   */
  roleLoading?: boolean;
  enabledFeatures: ReadonlySet<string>;
}

/** The single nav visibility rule — every surface filters through it. */
export function isNavItemVisible(item: NavGates, ctx: NavVisibilityContext): boolean {
  if (item.requiresSuperAdmin && !ctx.isSuperAdmin) return false;
  if (item.requiresOrgAdmin && !ctx.isAdmin && !ctx.isSuperAdmin) return false;
  if (
    item.requiresScope &&
    !ctx.roleLoading &&
    !ctx.can(item.requiresScope) &&
    !(item.superAdminBypass && ctx.isSuperAdmin)
  ) {
    return false;
  }
  if (item.featureFlag && !ctx.enabledFeatures.has(item.featureFlag)) return false;
  return true;
}

/** The caller's {@link NavVisibilityContext}, from the cached role, super-admin and feature queries. */
export function useNavVisibilityContext(): NavVisibilityContext {
  const { data: isSuperAdmin } = useIsSuperAdmin();
  const { isAdmin, can, isLoading: roleLoading } = useCurrentUserOrgRole();
  const enabledFeatures = useEnabledFeatures();
  return useMemo(
    () => ({ isSuperAdmin: isSuperAdmin === true, isAdmin, can, roleLoading, enabledFeatures }),
    [isSuperAdmin, isAdmin, can, roleLoading, enabledFeatures],
  );
}

/**
 * Drops `{ type: 'heading' }` entries whose section ended up empty after
 * filtering (e.g. the settings "Accounting" heading for a project manager).
 */
export function dropEmptyHeadings<T extends { type?: string }>(entries: T[]): T[] {
  return entries.filter((entry, i) => {
    if (entry.type !== 'heading') return true;
    const next = entries[i + 1];
    return next !== undefined && next.type !== 'heading';
  });
}
